import type { FunctionComponent } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { isNil } from 'lodash-es';

import type { AssetsTranscriptionElementType } from '@iconik-io/iconik-api/src/services/assets';
import type { Word } from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/types';
import { getProxyTimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_proxy_timecode_options';

import { Timecode } from '../../../components/fields/timecode/timecode';
import { updateTranscriptionWord } from '../../../ducks/asset_transcriptions/thunks';
import { selectAssetProxy } from '../../../ducks/pages/asset/selectors';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import TextFieldWrapper from '../../../ui/forms/field_wrappers/text/text';
import Form from '../../../ui/forms/form';
import IconButton from '../../../ui/icon_button/icon_button';

import mixins from '../../../styles/core/mixins.module.css';
import styles from './single_word_edit.module.css';

type Props = {
  word: Word;
};

const WordEditor: FunctionComponent<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { word } = props;
  const { id: assetId, versionId } = useParams({ strict: false });
  const assetProxy = useAppSelector((state) =>
    selectAssetProxy(state, assetId, versionId),
  );
  const segment = useAppSelector(
    (state) => state.entities.segments[word.segmentId],
  );

  let timeStart: number | undefined;
  let wordObject: AssetsTranscriptionElementType | undefined;

  if (segment && !isNil(segment.time_start_milliseconds)) {
    timeStart = segment.time_start_milliseconds;
  }
  if (segment && segment.transcription) {
    wordObject = segment.transcription.words[word.localIndex];
    if (wordObject && !isNil(segment.time_start_milliseconds)) {
      timeStart = segment.time_start_milliseconds + wordObject.start_ms;
    }
  }

  const onValidSubmit = ({ value }: { value: string }): void => {
    if (wordObject && wordObject.value === value) {
      return;
    }

    if (word.segmentId && word.localIndex !== undefined) {
      dispatch(
        updateTranscriptionWord(word.segmentId, word.localIndex, value, 1),
      );
    }
  };

  const timecodeOptions = assetProxy
    ? getProxyTimecodeOptions(assetProxy)
    : undefined;

  return wordObject ? (
    <div className={styles.root}>
      <Form className={styles.form} onValidSubmit={onValidSubmit}>
        <TextFieldWrapper
          className={styles.no_margin}
          name="value"
          value={wordObject.value}
        />
        <IconButton iconName="checkmark" type="submit" />
      </Form>
      {timecodeOptions ? (
        <button
          type="button"
          className={classNames(styles.word_time_start, mixins.reset_button)}
          data-segment-handler={word.segmentId}
          data-segment-start={timeStart}
        >
          <Timecode timecodeOptions={timecodeOptions} value={timeStart} />
        </button>
      ) : null}
    </div>
  ) : null;
};

export default WordEditor;
